import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EssayAttachmentPreview } from "@/components/ui/reader-ai/essay-attachment-preview";
import { EssayReadbackCard } from "@/components/ui/reader-ai/essay-readback-card";
import { GuidedStepCard } from "@/components/ui/reader-ai/guided-step-card";
import { GuidedStepFeedbackCard } from "@/components/ui/reader-ai/guided-step-feedback-card";
import { MarkdownMessage } from "@/components/ui/markdown-message";
import { ToofiInfoCue } from "@/components/ui/reader-ai/toofi-info-cue";
import { Textarea } from "@/components/ui/textarea";
import { TOOFI_CARD_VARIANT_CLASS, TOOFI_LABEL_CLASS } from "@/lib/toofi-ux-tokens";
import { cn } from "@/lib/utils";
import { buildToofiArtifactRecord, saveToofiArtifact } from "@/lib/toofi-artifact-store";
import type { ReaderAiBlock } from "@/types/agentic-chat";
import type { GuidedLessonControlInput, GuidedLessonSubmitInput } from "@/types/student-toofi";
import { toast } from "@/components/ui/sonner";
import { useEffect, useRef, useState } from "react";
import { Bookmark, CalendarClock, CheckCircle2, Circle, HelpCircle, ListChecks, NotebookPen, Brain, Target, XCircle } from "lucide-react";

type ReaderAiBlockRendererProps = {
  messageId?: string;
  threadId?: string;
  blockKey?: string;
  surfaceKind?: "reader" | "workspace";
  block: ReaderAiBlock;
  focusMode?: boolean;
  onGoToPage?: (pageNumber: number) => void;
  onMcqSubmit?: (
    blockId: string,
    selectedIndex: number,
    question?: {
      id: string;
      prompt: string;
      options: { id: string; label: string }[];
      correctIndex?: number | null;
      attemptId?: string | null;
      multi?: boolean;
    },
  ) => void;
  onFollowUpPrompt?: (prompt: string) => void;
  onEssayStart?: (blockId: string, prompt: string, minWords?: number, promptId?: string) => void;
  onEssayDraftChange?: (blockId: string, draft: string) => void;
  onEssayAttachmentAdd?: (blockId: string, file: File) => void;
  onEssayAttachmentRemove?: (blockId: string) => void;
  onEssayReadbackConfirm?: (blockId: string, confirmedText: string) => void;
  onEssaySubmit?: (blockId: string, prompt: string, draft: string, confirmedText?: string | null) => void;
  onGuidedLessonSubmit?: (input: GuidedLessonSubmitInput) => void;
  onGuidedLessonContinue?: (input: GuidedLessonControlInput) => void;
  onGuidedLessonSkip?: (input: GuidedLessonControlInput) => void;
  onPlanSave?: (messageId: string, blockId: string) => void;
  onPlanRefine?: (messageId: string, blockId: string) => void;
};

export function ReaderAiBlockRenderer({
  messageId,
  threadId,
  blockKey,
  surfaceKind = "reader",
  block,
  focusMode = false,
  onGoToPage,
  onMcqSubmit,
  onFollowUpPrompt,
  onEssayStart,
  onEssayDraftChange,
  onEssayAttachmentAdd,
  onEssayAttachmentRemove,
  onEssayReadbackConfirm,
  onEssaySubmit,
  onGuidedLessonSubmit,
  onGuidedLessonContinue,
  onGuidedLessonSkip,
  onPlanSave,
  onPlanRefine,
}: ReaderAiBlockRendererProps) {
  const [selectedIndexes, setSelectedIndexes] = useState<Record<string, number | null>>({});
  const [submittedIndexes, setSubmittedIndexes] = useState<Record<string, number | null>>({});
  const [solvedQuestions, setSolvedQuestions] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState("");
  const [savingArtifactKey, setSavingArtifactKey] = useState<string | null>(null);
  const [savedArtifactKeys, setSavedArtifactKeys] = useState<Record<string, boolean>>({});
  const essayAttachmentInputRef = useRef<HTMLInputElement | null>(null);
  const isRtlText = (value: string) => /[\u0590-\u08FF]/.test(value);
  const labelClass = TOOFI_LABEL_CLASS;
  const sectionTitleClass = focusMode
    ? "text-[14px] font-semibold tracking-[-0.015em] text-slate-900 lg:text-[16px]"
    : "text-[13px] font-semibold tracking-[-0.015em] text-slate-900 lg:text-[14px]";
  const bodyClass = focusMode
    ? "text-[13px] leading-7 text-slate-700 lg:text-[15px] lg:leading-8"
    : "text-[12px] leading-6 text-slate-700 lg:text-[13px] lg:leading-[1.8]";
  const metadataClass = "text-[10px] font-medium tracking-[0.02em] text-slate-500";
  const compactMarkdownClass =
    "max-w-none space-y-0.5 [&_p]:mb-0 [&_p]:text-[12px] [&_p]:leading-6 [&_li]:text-[12px] [&_li]:leading-6 [&_li]:marker:text-slate-400 [&_ol]:mb-0 [&_ol]:space-y-1 [&_ul]:mb-0 [&_ul]:space-y-1";
  const artifactCardClass = cn(
    TOOFI_CARD_VARIANT_CLASS.artifact,
    "space-y-2.5 p-2.5 sm:space-y-3 sm:p-3 lg:space-y-3.5 lg:p-3.5",
    focusMode && "max-w-[44rem] p-3 sm:p-3.5 lg:max-w-[46rem] lg:p-4",
  );
  const renderCompactMarkdown = (value: string, className?: string) => (
    <MarkdownMessage content={value} isRtl={isRtlText(value)} className={cn(compactMarkdownClass, className)} />
  );
  const formatDateLabel = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const formatMinutesLabel = (value?: number | null) => {
    if (!value || value <= 0) return null;
    return `${value} min`;
  };
  const buildMcqFollowUpPrompt = ({
    prompt,
    selected,
    correct,
    answeredCorrectly,
  }: {
    prompt: string;
    selected?: string | null;
    correct?: string | null;
    answeredCorrectly: boolean;
  }) => {
    const cleanedPrompt = prompt.trim();
    if (answeredCorrectly) {
      return `Continue this quiz with one harder question on the same topic. First give one short sentence on why "${correct ?? "the correct answer"}" fits best for "${cleanedPrompt}". Then give only the next question.`;
    }
    return `Continue this quiz on the same topic. First explain in 1-2 short sentences why "${selected ?? "my answer"}" is weaker and why "${correct ?? "the correct answer"}" is better for "${cleanedPrompt}". Then give one similar follow-up question that is slightly easier.`;
  };
  const sourcePage = block.type === "source" ? block.source.pageStart : null;
  const sourceDocId = block.type === "source" ? block.source.docId : null;
  const sourcePagePath = sourceDocId ? `/resource/${sourceDocId}?reader=1` : null;

  const handleSaveArtifact = async (candidate: ReaderAiBlock, candidateBlockId: string) => {
    if (!threadId || !messageId) return;
    const record = buildToofiArtifactRecord({
      block: candidate,
      blockId: candidateBlockId,
      threadId,
      messageId,
      scope: surfaceKind === "workspace" ? "workspace" : "reader",
      resourceId: sourceDocId,
      linkedPlanId: null,
      surfaceOrigin: surfaceKind === "workspace" ? "unknown" : "reader",
      sourcePage,
      sourcePageLabel: sourcePage ? `Page ${sourcePage}` : null,
      sourcePageRoute: sourcePagePath,
      sourcePageKind: surfaceKind,
      sourcePagePath,
    });
    if (!record) return;
    try {
      setSavingArtifactKey(candidateBlockId);
      await saveToofiArtifact(record);
      setSavedArtifactKeys((current) => ({ ...current, [candidateBlockId]: true }));
      toast.success("Saved to artifacts.");
    } catch (errorValue) {
      toast.error(errorValue instanceof Error ? errorValue.message : "Could not save this artifact.");
    } finally {
      setSavingArtifactKey(null);
    }
  };

  const renderArtifactSaveAction = (candidate: ReaderAiBlock, candidateBlockId: string) => (
    <Button
      type="button"
      variant="outline"
      className="h-8 rounded-full px-3 text-[11px]"
      disabled={!threadId || !messageId || savingArtifactKey === candidateBlockId}
      onClick={() => void handleSaveArtifact(candidate, candidateBlockId)}
    >
      <Bookmark className="mr-1.5 h-3.5 w-3.5" />
      {savedArtifactKeys[candidateBlockId] ? "Saved" : savingArtifactKey === candidateBlockId ? "Saving..." : "Save"}
    </Button>
  );

  useEffect(() => {
    if (block.type === "essay_workspace") {
      setDraft(block.draft);
    }
    if (block.type === "mcq") {
      const questionIds = block.questions?.map((question) => question.id) ?? [block.id];
      setSelectedIndexes(Object.fromEntries(questionIds.map((id) => [id, null])));
      setSubmittedIndexes(Object.fromEntries(questionIds.map((id) => [id, null])));
      setSolvedQuestions(Object.fromEntries(questionIds.map((id) => [id, false])));
    }
  }, [block]);

  if (block.type === "source") {
    const source = block.source;
    return (
      <div className="rounded-[0.95rem] border border-slate-200/50 bg-slate-50/70 p-2.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Source</p>
          <Button
            type="button"
            variant="ghost"
            className="h-7 rounded-full px-2 text-[10px] active:scale-95 transition-all duration-150 touch-manipulation"
            onClick={() => onGoToPage?.(source.pageStart)}
          >
            p.{source.pageStart}
          </Button>
        </div>
        <p
          dir={isRtlText(source.text) ? "rtl" : "auto"}
          lang={isRtlText(source.text) ? "ar" : "en"}
          className="mt-1 line-clamp-4 text-[11px] leading-5 text-foreground [unicode-bidi:plaintext]"
        >
          {source.text}
        </p>
      </div>
    );
  }

  if (block.type === "markdown") {
    return <MarkdownMessage content={block.markdown} isRtl={isRtlText(block.markdown)} className={focusMode ? "lg:max-w-5xl" : undefined} />;
  }

  if (block.type === "explanation") {
    return (
      <div className="space-y-1">
        <MarkdownMessage content={block.text} isRtl={isRtlText(block.text)} className={focusMode ? "lg:max-w-5xl" : undefined} />
      </div>
    );
  }

  if (block.type === "math") {
    return (
      <div className={artifactCardClass}>
        <Badge variant="outline" className={labelClass}>
          Math
        </Badge>
        <pre
          dir="ltr"
          className={cn(
            "overflow-x-auto rounded-[1rem] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.9))] px-3 py-3 font-mono text-[12px] text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.78),0_10px_24px_rgba(148,163,184,0.08)]",
            block.display ? "whitespace-pre-wrap break-words" : "whitespace-pre",
          )}
        >
          {block.latex}
        </pre>
        {block.explanation ? (
          <p
            dir={isRtlText(block.explanation) ? "rtl" : "auto"}
            lang={isRtlText(block.explanation) ? "ar" : "en"}
            className={`${bodyClass} rounded-[0.95rem] border border-slate-200/60 bg-white/78 px-3 py-2.5 [unicode-bidi:plaintext]`}
          >
            {block.explanation}
          </p>
        ) : null}
      </div>
    );
  }

  if (block.type === "formula_sheet") {
    return (
      <div className={cn(artifactCardClass, "space-y-2", focusMode && "max-w-4xl p-3.5")}>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={labelClass}>
            Formula Sheet
          </Badge>
          <ToofiInfoCue explanation="Keeps the key equations and labels in one compact reference card." />
          {renderArtifactSaveAction(block, blockKey ?? "formula-sheet")}
        </div>
        {block.title ? <p className={sectionTitleClass}>{block.title}</p> : null}
        <div className="space-y-2">
          {block.items.map((item, index) => (
            <div key={`${item.label}-${index}`} className="rounded-[0.95rem] border border-slate-200/60 bg-white/90 px-3 py-2.5">
              <p className="text-[11px] font-semibold text-slate-900">{item.label}</p>
              {item.latex ? (
                <div className="mt-1 overflow-x-auto rounded-[0.75rem] bg-slate-950 px-2.5 py-2 font-mono text-[11px] text-slate-100">
                  {item.latex}
                </div>
              ) : null}
              {item.text ? <p className="mt-1 text-[12px] leading-6 text-slate-700">{item.text}</p> : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (block.type === "translation_compare") {
    return (
      <div className={cn(artifactCardClass, "space-y-2", focusMode && "max-w-4xl")}>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={labelClass}>
            Translation
          </Badge>
          <ToofiInfoCue explanation="Shows the original and translated versions side by side." />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-[1rem] border border-slate-200/50 bg-slate-50/80 p-2.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">Original</p>
            <p
              dir={isRtlText(block.original) ? "rtl" : "auto"}
              lang={isRtlText(block.original) ? "ar" : "en"}
              className="mt-1 text-[12px] leading-6 text-slate-700 [unicode-bidi:plaintext]"
            >
              {block.original}
            </p>
          </div>
          <div className="rounded-[1rem] border border-slate-200/50 bg-white/80 p-2.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">Translated</p>
            <p
              dir={isRtlText(block.translated) ? "rtl" : "auto"}
              lang={isRtlText(block.translated) ? "ar" : "en"}
              className="mt-1 text-[12px] leading-6 text-slate-800 [unicode-bidi:plaintext]"
            >
              {block.translated}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (block.type === "worked_example") {
    return (
      <div className={artifactCardClass}>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={labelClass}>
            Worked Example
          </Badge>
          <ToofiInfoCue explanation="Breaks the solution into concrete steps so you can follow the logic." />
          {renderArtifactSaveAction(block, blockKey ?? "worked-example")}
        </div>
        {block.title ? <p className={sectionTitleClass}>{block.title}</p> : null}
        <ol className="space-y-2">
          {block.steps.map((step, index) => (
            <li key={`worked-step-${index}`} className="flex gap-2.5 rounded-[0.95rem] border border-slate-200/60 bg-white/88 px-3 py-2.5 text-[12px] leading-6 text-slate-700">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1 [unicode-bidi:plaintext]">{renderCompactMarkdown(step)}</div>
            </li>
          ))}
        </ol>
        {block.takeaway ? (
          <div className="rounded-[0.95rem] border border-slate-200/60 bg-white px-3 py-2.5 text-[12px] leading-6 text-slate-800">
            <span className="font-semibold text-slate-900">Takeaway:</span>{" "}
            <div className="mt-1 [unicode-bidi:plaintext]">{renderCompactMarkdown(block.takeaway)}</div>
          </div>
        ) : null}
      </div>
    );
  }

  if (block.type === "guided_step") {
    return (
      <GuidedStepCard
        block={block}
        onSubmit={onGuidedLessonSubmit}
        onSkip={onGuidedLessonSkip}
      />
    );
  }

  if (block.type === "guided_step_feedback") {
    return (
      <GuidedStepFeedbackCard
        block={block}
        onContinue={onGuidedLessonContinue}
      />
    );
  }

  if (block.type === "summary") {
    return (
      <div className={cn(artifactCardClass, "space-y-2", focusMode && "max-w-4xl")}>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={labelClass}>
            Summary
          </Badge>
          <ToofiInfoCue explanation="A short set of takeaways you can scan quickly before moving on." />
          {renderArtifactSaveAction(block, blockKey ?? "summary")}
        </div>
        <ul className="list-disc space-y-1.5 ps-5 text-[12px] leading-6 text-slate-700">
          {block.items.map((item, index) => (
            <li key={`summary-${index}`} className="[unicode-bidi:plaintext]">
              {renderCompactMarkdown(item)}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (block.type === "plan_overview") {
    const targetDateLabel = formatDateLabel(block.targetDate);
    const durationLabel = formatMinutesLabel(block.estimatedTotalMinutes);
    const statusTone =
      block.status === "active"
        ? "border-emerald-200/80 bg-emerald-50/75 text-emerald-800"
        : block.status === "completed"
          ? "border-slate-200/80 bg-slate-100/80 text-slate-700"
          : "border-amber-200/80 bg-amber-50/75 text-amber-800";

    return (
      <div className={cn(artifactCardClass, "space-y-3 border-amber-100/80 shadow-[0_18px_36px_rgba(217,119,6,0.08)]")}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={labelClass}>
                Study Plan
              </Badge>
              <ToofiInfoCue explanation="The main plan card: goal, horizon, load, and the actions you can save or refine." />
              {renderArtifactSaveAction(block, block.id)}
            </div>
            <p className={cn(sectionTitleClass, "max-w-2xl leading-6")}>{block.title}</p>
          </div>
          <span className={cn("inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold capitalize", statusTone)}>
            {block.status ?? "draft"}
          </span>
        </div>
        <p
          dir={isRtlText(block.goalSummary) ? "rtl" : "auto"}
          lang={isRtlText(block.goalSummary) ? "ar" : "en"}
          className={`${bodyClass} [unicode-bidi:plaintext]`}
        >
          {block.goalSummary}
        </p>
        <div className="grid gap-2 sm:grid-cols-3">
          <div className="rounded-[1rem] border border-slate-200/70 bg-white/90 px-3 py-2.5">
            <div className="flex items-center gap-2 text-slate-500">
              <Target className="h-3.5 w-3.5 text-amber-700" />
              <span className={metadataClass}>Horizon</span>
            </div>
            <p className="mt-1 text-[12px] font-semibold text-slate-900">{block.horizonLabel}</p>
          </div>
          <div className="rounded-[1rem] border border-slate-200/70 bg-white/90 px-3 py-2.5">
            <div className="flex items-center gap-2 text-slate-500">
              <CalendarClock className="h-3.5 w-3.5 text-amber-700" />
              <span className={metadataClass}>Target date</span>
            </div>
            <p className="mt-1 text-[12px] font-semibold text-slate-900">{targetDateLabel ?? "Flexible"}</p>
          </div>
          <div className="rounded-[1rem] border border-slate-200/70 bg-white/90 px-3 py-2.5">
            <div className="flex items-center gap-2 text-slate-500">
              <Brain className="h-3.5 w-3.5 text-amber-700" />
              <span className={metadataClass}>Estimated load</span>
            </div>
            <p className="mt-1 text-[12px] font-semibold text-slate-900">{durationLabel ?? "Adaptive"}</p>
          </div>
        </div>
        {block.confirmationRequired && messageId ? (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              className="h-9 rounded-full px-4 text-[11px] active:scale-95 transition-all duration-150 touch-manipulation"
              onClick={() => onPlanSave?.(messageId, block.id)}
            >
              Save as active plan
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-full px-4 text-[11px] active:scale-95 transition-all duration-150 touch-manipulation"
              onClick={() => onPlanRefine?.(messageId, block.id)}
            >
              Refine plan
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  if (block.type === "plan_milestones") {
    return (
      <div className={artifactCardClass}>
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-amber-700" />
          <p className="text-[11px] font-semibold tracking-[0.02em] text-slate-700">Milestones</p>
          <ToofiInfoCue explanation="Breaks the plan into checkpoints so you can see what matters now and what comes next." />
          {renderArtifactSaveAction(block, blockKey ?? "plan-milestones")}
        </div>
        <div className="space-y-2.5">
          {block.items.map((item, index) => {
            const statusTone =
              item.status === "completed"
                ? "border-emerald-200 bg-emerald-50/85 text-emerald-800"
                : item.status === "active"
                  ? "border-amber-200 bg-amber-50/80 text-amber-800"
                  : item.status === "skipped"
                    ? "border-slate-200 bg-slate-100/90 text-slate-500"
                    : "border-slate-200/70 bg-white/92 text-slate-800";

            return (
              <div key={item.id} className={cn("rounded-[1rem] border px-3 py-3", statusTone)}>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/80 text-[10px] font-semibold text-slate-700 shadow-sm">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[12px] font-semibold text-slate-900">{item.title}</p>
                      <span className="rounded-full bg-white/80 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                        {item.status ?? "pending"}
                      </span>
                    </div>
                    {item.summary ? (
                      <div className="mt-1 text-slate-600 [unicode-bidi:plaintext]">
                        {renderCompactMarkdown(item.summary, "[&_p]:text-[11px] [&_p]:leading-5 [&_li]:text-[11px] [&_li]:leading-5")}
                      </div>
                    ) : null}
                    {item.targetDate ? (
                      <p className="mt-1 text-[10px] font-medium text-slate-500">Target: {formatDateLabel(item.targetDate)}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (block.type === "daily_plan") {
    return (
      <div className={artifactCardClass}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-amber-700" />
            <div>
              <p className="text-[11px] font-semibold tracking-[0.02em] text-slate-700">Today&apos;s plan</p>
              <p className="text-[10px] text-slate-500">{formatDateLabel(block.date) ?? block.date}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {renderArtifactSaveAction(block, blockKey ?? "daily-plan")}
            {block.totalMinutes ? (
              <span className="rounded-full border border-amber-100 bg-amber-50/80 px-2.5 py-1 text-[10px] font-semibold text-amber-800">
                {block.totalMinutes} min
              </span>
            ) : null}
          </div>
        </div>
        <div className="space-y-2">
          {block.items.map((item) => (
            <div key={item.id} className="rounded-[1rem] border border-slate-200/70 bg-white/92 px-3 py-2.5">
              <div className="flex items-start gap-2.5">
                <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold", item.checked ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-slate-300 bg-slate-100 text-slate-500")}>
                  {item.checked ? "✓" : ""}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-700 [unicode-bidi:plaintext]">{renderCompactMarkdown(item.text)}</div>
                  {(item.estimatedMinutes || item.resourceTitle) ? (
                    <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-slate-500">
                      {item.estimatedMinutes ? <span>{item.estimatedMinutes} min</span> : null}
                      {item.resourceTitle ? <span>{item.resourceTitle}</span> : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (block.type === "plan_checkin") {
    const tone =
      block.status === "completed"
        ? "border-emerald-200 bg-emerald-50/80"
        : block.status === "behind" || block.status === "at_risk"
          ? "border-amber-200 bg-amber-50/80"
          : "border-slate-200 bg-slate-100/80";

    return (
      <div className={cn("space-y-2 rounded-[1rem] border px-3 py-3", tone)}>
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-amber-700" />
          <p className="text-[11px] font-semibold text-slate-800">{block.title ?? "Plan check-in"}</p>
          {renderArtifactSaveAction(block, blockKey ?? "plan-checkin")}
        </div>
        <div className="text-slate-700 [unicode-bidi:plaintext]">{renderCompactMarkdown(block.summary)}</div>
        {block.nextAction ? <p className="text-[11px] font-medium text-slate-600">Next: {block.nextAction}</p> : null}
      </div>
    );
  }

  if (block.type === "comparison") {
    return (
      <div className={artifactCardClass}>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={labelClass}>
            Comparison
          </Badge>
          {renderArtifactSaveAction(block, blockKey ?? "comparison")}
        </div>
        {block.title ? <p className={sectionTitleClass}>{block.title}</p> : null}
        <div className="overflow-x-auto rounded-[0.95rem] border border-slate-200/70 bg-white/92">
          <table className="w-full min-w-[20rem] text-left text-[11px] text-slate-700 sm:min-w-[24rem]">
            <thead className="bg-slate-100/90">
              <tr>
                <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Topic</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{block.leftLabel}</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{block.rightLabel}</th>
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, index) => (
                <tr key={`${row.topic}-${index}`} className="border-t border-slate-200/60">
                  <td className="px-3 py-2.5 align-top font-medium text-slate-900 [unicode-bidi:plaintext]">
                    {renderCompactMarkdown(row.topic, "[&_p]:font-semibold [&_p]:text-slate-900")}
                  </td>
                  <td className="px-3 py-2.5 align-top leading-6 [unicode-bidi:plaintext]">{renderCompactMarkdown(row.left)}</td>
                  <td className="px-3 py-2.5 align-top leading-6 [unicode-bidi:plaintext]">{renderCompactMarkdown(row.right)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (block.type === "mcq") {
    const questions =
      block.questions?.length
        ? block.questions
        : block.prompt && block.options
          ? [
              {
                id: block.id,
                prompt: block.prompt,
                options: block.options,
                correctIndex: block.correctIndex,
                attemptId: block.attemptId,
                multi: block.multi,
              },
            ]
          : [];

    return (
      <div className={cn(artifactCardClass, "space-y-3")}>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={labelClass}>
            {questions.length > 1 ? "Question Set" : "MCQ"}
          </Badge>
          {questions.length > 1 ? (
            <ToofiInfoCue explanation="Keeps a whole question run in one block so you can work through the set without breaking the thread." />
          ) : null}
          {renderArtifactSaveAction(block, block.id ?? blockKey ?? "mcq")}
        </div>
        {questions.map((question, questionIndex) => {
          const selectedIndex = selectedIndexes[question.id] ?? null;
          const submittedIndex = submittedIndexes[question.id] ?? null;
          const isSolved = solvedQuestions[question.id] ?? false;
          const hasLocalAnswerKey = typeof question.correctIndex === "number";
          const didSubmit = submittedIndex !== null;
          const isCorrect = hasLocalAnswerKey && submittedIndex === question.correctIndex;
          const solvedTone = isSolved
            ? "border-emerald-200/80 bg-emerald-50/70 shadow-[0_10px_24px_rgba(16,185,129,0.10)]"
            : didSubmit
              ? "border-amber-200/80 bg-amber-50/70 shadow-[0_10px_24px_rgba(245,158,11,0.08)]"
              : "border-slate-200/50 bg-white/80 shadow-[0_6px_18px_rgba(15,23,42,0.03)]";

          return (
            <div
              key={question.id}
              className={cn(
                "space-y-3 rounded-[1rem] border p-2.5 transition-all duration-200 sm:rounded-[1.15rem] sm:p-3",
                solvedTone,
                focusMode && "max-w-4xl sm:p-3.5",
              )}
            >
              {questions.length > 1 ? (
                <div className="flex items-center justify-between gap-2">
                  <p className={metadataClass}>Question {questionIndex + 1}</p>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    {isSolved ? "Solved" : didSubmit ? "Checked" : "Open"}
                  </span>
                </div>
              ) : null}
              <p
                dir={isRtlText(question.prompt) ? "rtl" : "auto"}
                lang={isRtlText(question.prompt) ? "ar" : "en"}
                className={`${sectionTitleClass} leading-6 [unicode-bidi:plaintext]`}
              >
                {question.prompt}
              </p>
              <div className="space-y-2.5">
                {question.options.map((opt, index) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      if (isSolved) return;
                      setSelectedIndexes((current) => ({ ...current, [question.id]: index }));
                    }}
                    className={cn(
                      "w-full rounded-[0.95rem] border px-3 py-2.5 text-left text-[12px] leading-5 transition-all duration-150 touch-manipulation sm:rounded-[1rem] sm:py-3",
                      isSolved && question.correctIndex === index
                        ? "border-emerald-300 bg-emerald-100 text-emerald-950 shadow-sm"
                        : didSubmit && submittedIndex === index && question.correctIndex !== index
                          ? "border-rose-300 bg-rose-100 text-rose-950 shadow-sm"
                          : selectedIndex === index
                            ? "border-amber-200 bg-amber-50/80 text-slate-900 shadow-sm"
                            : "border-slate-200 bg-white/90 text-slate-700 hover:bg-slate-50/90",
                      isSolved ? "cursor-default" : "",
                    )}
                    dir={isRtlText(opt.label) ? "rtl" : "auto"}
                    lang={isRtlText(opt.label) ? "ar" : "en"}
                    disabled={isSolved}
                  >
                    <span className="flex items-start gap-2.5">
                      <span
                        className={cn(
                          "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold",
                          isSolved && question.correctIndex === index
                            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                            : didSubmit && submittedIndex === index && question.correctIndex !== index
                              ? "border-rose-300 bg-rose-50 text-rose-700"
                              : selectedIndex === index
                                ? "border-amber-200 bg-amber-100 text-amber-800"
                                : "border-slate-300 bg-slate-50 text-slate-500",
                        )}
                      >
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="[unicode-bidi:plaintext]">{opt.label}</span>
                    </span>
                  </button>
                ))}
              </div>
              {hasLocalAnswerKey ? (
                <>
                  <Button
                    type="button"
                    className="h-9 rounded-full px-4 text-[11px] active:scale-95 transition-all duration-150 touch-manipulation"
                    disabled={selectedIndex === null || isSolved}
                    onClick={() => {
                      if (selectedIndex === null) return;
                      setSubmittedIndexes((current) => ({ ...current, [question.id]: selectedIndex }));
                      if (selectedIndex === question.correctIndex) {
                        setSolvedQuestions((current) => ({ ...current, [question.id]: true }));
                      }
                    }}
                  >
                    {isSolved ? "Answered correctly" : "Check answer"}
                  </Button>
                  {didSubmit ? (
                    <div
                      className={cn(
                        "animate-in fade-in-0 slide-in-from-bottom-1 rounded-[1rem] border px-3 py-3 text-[12px] leading-6 duration-200",
                        isCorrect
                          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                          : "border-amber-200 bg-amber-50 text-amber-900",
                      )}
                    >
                      {isCorrect ? (
                        <p>
                          Correct. This option fits the question best. Before moving on, make sure you can say why the other choices are weaker or incomplete.
                        </p>
                      ) : (
                        <p>
                          Incorrect. You picked{" "}
                          <span className="font-semibold">
                            {submittedIndex !== null ? question.options[submittedIndex]?.label : ""}
                          </span>
                          . The stronger answer is{" "}
                          <span className="font-semibold">
                            {typeof question.correctIndex === "number" ? question.options[question.correctIndex]?.label : ""}
                          </span>
                          . Re-check the wording of the prompt and look for the option that matches it most directly, not just a related idea.
                        </p>
                      )}
                    </div>
                  ) : null}
                  {questions.length === 1 && didSubmit && onFollowUpPrompt ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-9 rounded-full px-4 text-[11px] active:scale-95 transition-all duration-150 touch-manipulation"
                      onClick={() =>
                        onFollowUpPrompt(
                          buildMcqFollowUpPrompt({
                            prompt: question.prompt,
                            selected: submittedIndex !== null ? question.options[submittedIndex]?.label : null,
                            correct: typeof question.correctIndex === "number" ? question.options[question.correctIndex]?.label : null,
                            answeredCorrectly: Boolean(isCorrect),
                          }),
                        )
                      }
                    >
                      Next question
                    </Button>
                  ) : null}
                </>
              ) : (
                <Button
                  type="button"
                  className="h-9 rounded-full px-4 text-[11px] active:scale-95 transition-all duration-150 touch-manipulation"
                  disabled={selectedIndex === null}
                  onClick={() => selectedIndex !== null && onMcqSubmit?.(block.id, selectedIndex, question)}
                >
                  Submit answer
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (block.type === "essay_prompt") {
    const prompts =
      block.prompts?.length
        ? block.prompts
        : block.prompt
          ? [{ id: block.id, prompt: block.prompt, minWords: block.minWords }]
          : [];

    return (
      <div className={artifactCardClass}>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={labelClass}>
            {prompts.length > 1 ? "Essay Set" : "Essay Prompt"}
          </Badge>
          {prompts.length > 1 ? (
            <ToofiInfoCue explanation="Keeps several writing prompts in one calm set so you can pick one without losing the rest." />
          ) : null}
          {renderArtifactSaveAction(block, block.id ?? blockKey ?? "essay-prompt")}
        </div>
        <div className="space-y-2.5">
          {prompts.map((promptItem, promptIndex) => (
            <div
              key={promptItem.id}
              className="space-y-2.5 rounded-[0.95rem] border border-slate-200/60 bg-white/88 px-3 py-3 shadow-[0_8px_18px_rgba(148,163,184,0.06)]"
            >
              <div className="flex items-center justify-between gap-2">
                {prompts.length > 1 ? <p className={metadataClass}>Prompt {promptIndex + 1}</p> : <span />}
                <span className="text-[10px] font-medium text-slate-400">
                  {promptItem.minWords ? `Min ${promptItem.minWords} words` : "Open writing workspace"}
                </span>
              </div>
              <p
                dir={isRtlText(promptItem.prompt) ? "rtl" : "auto"}
                lang={isRtlText(promptItem.prompt) ? "ar" : "en"}
                className={`${bodyClass} [unicode-bidi:plaintext]`}
              >
                {promptItem.prompt}
              </p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] text-slate-500">
                  {prompts.length > 1 ? "Choose one prompt to open its writing space." : promptItem.minWords ? `Minimum ${promptItem.minWords} words` : "Open a writing workspace"}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 rounded-full px-4 text-[11px] active:scale-95 transition-all duration-150 touch-manipulation"
                  onClick={() => onEssayStart?.(block.id, promptItem.prompt, promptItem.minWords, promptItem.id)}
                >
                  Start writing
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (block.type === "essay_workspace") {
    return (
      <div className={cn("space-y-2.5 rounded-[1rem] border border-slate-200/50 bg-slate-50/80 p-2.5", focusMode && "max-w-4xl p-3.5")}>
        <div className="flex items-center gap-2">
          <NotebookPen className="h-3.5 w-3.5 text-amber-700" />
          <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">Drafting Workspace</p>
        </div>
        <p
          dir={isRtlText(block.prompt) ? "rtl" : "auto"}
          lang={isRtlText(block.prompt) ? "ar" : "en"}
          className={`${sectionTitleClass} leading-6 [unicode-bidi:plaintext]`}
        >
          {block.prompt}
        </p>
        <Textarea
          value={draft}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            onEssayDraftChange?.(block.id, next);
          }}
          placeholder="Write your answer here..."
          className="min-h-[130px] rounded-[0.95rem] border-white/70 bg-white/90 text-[12px] leading-5"
        />
        <input
          ref={essayAttachmentInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              onEssayAttachmentAdd?.(block.id, file);
            }
            event.currentTarget.value = "";
          }}
        />
        <EssayAttachmentPreview
          attachment={block.attachment}
          onAdd={() => essayAttachmentInputRef.current?.click()}
          onReplace={() => essayAttachmentInputRef.current?.click()}
          onRemove={() => onEssayAttachmentRemove?.(block.id)}
        />
        {block.readback && ["recognized", "confirmed", "edited"].includes(block.readback.status) ? (
          <EssayReadbackCard
            readback={block.readback}
            onConfirm={(confirmedText) => onEssayReadbackConfirm?.(block.id, confirmedText)}
            onRetake={() => essayAttachmentInputRef.current?.click()}
          />
        ) : null}
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-500">{draft.trim().split(/\s+/).filter(Boolean).length} words</p>
          <Button
            type="button"
            className="h-9 rounded-full text-[11px] active:scale-95 transition-all duration-150 touch-manipulation"
            disabled={!draft.trim() && !block.attachment}
            onClick={() =>
              onEssaySubmit?.(
                block.id,
                block.prompt,
                draft,
                block.readback?.status === "confirmed" || block.readback?.status === "edited"
                  ? block.readback.extractedText
                  : null,
              )
            }
          >
            {block.attachment && (!block.readback || block.readback.status === "pending") ? "Read photo" : "Submit draft"}
          </Button>
        </div>
      </div>
    );
  }

  if (block.type === "essay_evaluation") {
    return (
      <div className={cn("space-y-2.5 rounded-[1.1rem] border border-slate-200/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(248,250,252,0.82))] p-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]", focusMode && "max-w-4xl p-3.5")}>
        <Badge variant="outline" className={labelClass}>
          Essay Evaluation
        </Badge>
        {block.score ? <p className={sectionTitleClass}>Score: {block.score}</p> : null}
        <div className="space-y-2">
          <p className={bodyClass}><span className="font-semibold text-slate-900">Highlights:</span> {block.highlights}</p>
          <p className={bodyClass}><span className="font-semibold text-slate-900">Missing:</span> {block.missing}</p>
          <p className={bodyClass}><span className="font-semibold text-slate-900">Model answer:</span> {block.modelAnswer}</p>
        </div>
      </div>
    );
  }

  if (block.type === "note") {
    return (
      <div className={cn("space-y-2.5 rounded-[1.15rem] border border-slate-200/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(247,250,252,0.86))] p-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]", focusMode && "max-w-4xl p-3.5")}>
        <div className="flex items-center gap-2">
          <NotebookPen className="h-3.5 w-3.5 text-amber-700" />
          <p className="text-[10px] font-semibold text-slate-700">{block.title}</p>
          {renderArtifactSaveAction(block, block.id)}
        </div>
        <MarkdownMessage content={block.content} isRtl={isRtlText(block.content)} className={focusMode ? "lg:max-w-5xl" : undefined} />
      </div>
    );
  }

  if (block.type === "table") {
    return (
      <div className={cn("overflow-x-auto rounded-[1.1rem] border border-slate-200/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(248,250,252,0.84))] p-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]", focusMode && "max-w-4xl p-3")}>
        <div className="mb-2 flex items-center gap-2 px-1">
          <Badge variant="outline" className={labelClass}>
            Table
          </Badge>
          {renderArtifactSaveAction(block, block.id)}
        </div>
        <table className="w-full min-w-[18rem] text-left text-[11px] text-slate-700">
          <thead className="bg-slate-100/90">
            <tr>
              {block.columns.map((column) => (
                <th key={column} className="border-b border-slate-200 px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIdx) => (
              <tr key={`row-${rowIdx}`}>
                {row.map((value, cellIdx) => (
                  <td key={`cell-${cellIdx}`} className="border-b border-slate-100 px-3 py-2.5 align-top leading-6 [unicode-bidi:plaintext]">
                    {renderCompactMarkdown(value)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (block.type === "checklist") {
    return (
      <div className={artifactCardClass}>
        <Badge variant="outline" className={labelClass}>
          Plan
        </Badge>
        {block.title ? <p className={sectionTitleClass}>{block.title}</p> : null}
        <div className="space-y-2">
          {block.items.map((item) => (
            <div key={item.id} className="flex items-start gap-2.5 rounded-[1rem] border border-slate-200/60 bg-white/92 px-3 py-2.5">
              <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold", item.checked ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-slate-300 bg-slate-100 text-slate-500")}>
                {item.checked ? "✓" : ""}
              </span>
              <div className="min-w-0 flex-1 text-slate-700">{renderCompactMarkdown(item.text)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (block.type === "reflection") {
    return (
      <div className={cn("space-y-1.5 rounded-[1rem] border border-amber-200/60 bg-amber-50/60 px-3 py-2.5", focusMode && "max-w-4xl px-3.5 py-3")}>
        <p
          dir={isRtlText(block.prompt) ? "rtl" : "auto"}
          lang={isRtlText(block.prompt) ? "ar" : "en"}
          className={`${bodyClass} [unicode-bidi:plaintext]`}
        >
          {block.prompt}
        </p>
      </div>
    );
  }

  const tone =
    block.kind === "success"
      ? "border-emerald-100 bg-emerald-50/70 text-emerald-900"
      : block.kind === "error"
        ? "border-rose-100 bg-rose-50/70 text-rose-900"
        : "border-amber-100/80 bg-amber-50/75 text-amber-900";
  const icon =
    block.kind === "success" ? (
      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
    ) : block.kind === "error" ? (
      <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600" />
    ) : (
      <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
    );

  return (
    <div className={cn("flex items-start gap-2 rounded-[0.95rem] border px-3 py-2 text-[11px] leading-5", tone)}>
      {icon}
      <p
        dir={isRtlText(block.text) ? "rtl" : "auto"}
        lang={isRtlText(block.text) ? "ar" : "en"}
        className="[unicode-bidi:plaintext]"
      >
        {block.text}
      </p>
    </div>
  );
}
